import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, AreaChart, Area, XAxis, YAxis } from "recharts";
import { Receipt, Plus, Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import { API } from "../../../App";
import { getAuthCfg, money, Panel, InlineError, Overline, CHART_COLORS } from "./singleUtils";

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-sm border border-border/60 bg-muted/30 px-2.5 py-2" data-testid={testid}>
    <p className="text-sm font-extrabold font-mono text-foreground leading-tight truncate">{value}</p>
    <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mt-0.5">{label}</p>
    {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
  </div>
);

const ExpenseTrend = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/store/dashboard-v2/chart`, { ...getAuthCfg(), params: { metric: "expenses", days } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);
  const rows = (data?.labels || []).map((day, i) => ({ day: day.slice(5), v: data?.series?.[0]?.data?.[i] || 0 }));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Overline>Expenses Over Time</Overline>
        <div className="flex rounded-sm border border-border overflow-hidden">
          {[7, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-1.5 py-px text-[9px] font-bold font-mono ${days === d ? "bg-amber-500 text-black" : "text-muted-foreground"}`}
              data-testid={`expense-trend-days-${d}`}>{d}D</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="h-[92px] flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-amber-500" /></div>
      ) : (
        <div className="h-[92px]" data-testid="expense-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 4, right: 2, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="expTrend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e11d48" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#e11d48" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis hide />
              <Tooltip formatter={(v) => [money(v), "Expenses"]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
              <Area type="monotone" dataKey="v" stroke="#e11d48" strokeWidth={1.5} fill="url(#expTrend)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Expenses Overview — the single-location money-out command view.
const ExpenseCenter = ({ expenses, dateFilter, customStart, customEnd, onAddExpense, onRetry }) => {
  const navigate = useNavigate();
  if (!expenses) return null;
  if (expenses.error) {
    return <Panel title="Expenses Overview" icon={Receipt} testid="expenses-overview"><InlineError label="expense data" onRetry={onRetry} /></Panel>;
  }
  const cats = expenses.by_category || [];
  const total = expenses.total || 0;
  const openCategory = (cat) => {
    const p = new URLSearchParams({ category: cat, date_filter: dateFilter });
    if (dateFilter === "custom") { p.set("start_date", customStart); p.set("end_date", customEnd); }
    navigate(`/dashboard/expenses?${p.toString()}`);
  };
  const openAll = () => {
    const p = new URLSearchParams({ date_filter: dateFilter });
    if (dateFilter === "custom") { p.set("start_date", customStart); p.set("end_date", customEnd); }
    navigate(`/dashboard/expenses?${p.toString()}`);
  };
  return (
    <Panel title="Expenses Overview" icon={Receipt} testid="expenses-overview"
      right={
        <div className="flex items-center gap-2">
          <button onClick={openAll} className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:underline" data-testid="expenses-view-all">View All →</button>
          <Button size="sm" className="h-6 px-2 text-[11px] bg-amber-500 hover:bg-amber-400 text-black font-semibold" onClick={onAddExpense} data-testid="expenses-add-btn">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      }>
      {/* Stats row */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-3">
        <Stat label="This Period" value={money(total)} testid="exp-stat-total" />
        <Stat label="Today" value={money(expenses.today_total)} testid="exp-stat-today" />
        <Stat label="This Month" value={money(expenses.month_total)} testid="exp-stat-month" />
        <Stat label="Avg / Day" value={money(expenses.avg_daily)} testid="exp-stat-avg" />
        <Stat label="Top Category" value={expenses.top_category ? money(expenses.top_category.amount) : "—"} sub={expenses.top_category?.category} testid="exp-stat-top-cat" />
        <Stat label="Largest" value={expenses.largest ? money(expenses.largest.amount) : "—"} sub={expenses.largest?.description} testid="exp-stat-largest" />
      </div>

      {total === 0 && (expenses.recent || []).length === 0 ? (
        <div className="text-center py-6" data-testid="expenses-empty">
          <p className="text-xs text-muted-foreground mb-3">No expenses recorded in this period</p>
          <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" onClick={onAddExpense} data-testid="expenses-empty-add">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Expense
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Category breakdown donut */}
          <div>
            <Overline>Breakdown by Category</Overline>
            <div className="flex items-center gap-3 mt-1">
              <div className="relative w-[120px] h-[120px] shrink-0" data-testid="expense-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={cats} dataKey="amount" nameKey="category" innerRadius={40} outerRadius={57} paddingAngle={2} stroke="none">
                      {cats.map((c, i) => (
                        <Cell key={c.category} fill={CHART_COLORS[i % CHART_COLORS.length]} cursor="pointer" onClick={() => openCategory(c.category)} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [money(v), n]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-[13px] font-extrabold font-mono text-foreground">{money(total)}</span>
                </div>
              </div>
              <div className="flex-1 min-w-0 space-y-0.5 max-h-[120px] overflow-y-auto pr-1">
                {cats.slice(0, 7).map((c, i) => (
                  <button key={c.category} onClick={() => openCategory(c.category)}
                    className="w-full flex items-center justify-between gap-2 text-left hover:bg-muted/40 rounded-sm px-1 py-0.5"
                    data-testid={`expense-cat-${c.category.replace(/\s+/g, "-").toLowerCase()}`}>
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="w-2 h-2 rounded-[2px] shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-[11px] text-foreground truncate">{c.category}</span>
                    </span>
                    <span className="text-[11px] font-mono font-semibold text-muted-foreground shrink-0">
                      {money(c.amount)} <span className="opacity-60">({total > 0 ? Math.round((c.amount / total) * 100) : 0}%)</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* Trend + recent */}
          <div className="space-y-3">
            <ExpenseTrend />
            <div>
              <Overline>Recent Expenses</Overline>
              <div className="mt-1 divide-y divide-border/40" data-testid="recent-expenses-list">
                {(expenses.recent || []).length === 0 ? (
                  <p className="text-[11px] text-muted-foreground py-2">None in this period</p>
                ) : (expenses.recent || []).slice(0, 4).map((e) => (
                  <div key={e.expense_id} className="flex items-center justify-between gap-2 py-1.5">
                    <div className="min-w-0">
                      <p className="text-xs text-foreground truncate">{e.description}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{e.date} · {e.category}{e.recurring_source_id || e.is_recurring ? " · recurring" : ""}</p>
                    </div>
                    <span className="text-xs font-bold font-mono text-rose-600 dark:text-rose-500 shrink-0">{money(e.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
};

export default ExpenseCenter;
